import PropTypes from 'prop-types';
import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { grayLightM } from 'web-ui-kit/colors';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject, _templateObject2, _templateObject3;
/**
 * Simple rectangle with skeleton loading animation.
 * Combine multiple bones together to outline the shape of your content.
 */


function Bone(_ref) {
  var _ref$animate = _ref.animate,
      animate = _ref$animate === void 0 ? true : _ref$animate,
      _ref$animDelay = _ref.animDelay,
      animDelay = _ref$animDelay === void 0 ? 0 : _ref$animDelay,
      _ref$animSpeed = _ref.animSpeed,
      animSpeed = _ref$animSpeed === void 0 ? 1.4 : _ref$animSpeed,
      _ref$bgColor = _ref.bgColor,
      bgColor = _ref$bgColor === void 0 ? grayLightM : _ref$bgColor,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dataTestId = _ref.dataTestId,
      dataTestId = _ref$dataTestId === void 0 ? undefined : _ref$dataTestId,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? '200px' : _ref$height,
      _ref$highlightColor = _ref.highlightColor,
      highlightColor = _ref$highlightColor === void 0 ? 'rgba(255, 255, 255, 0.5)' : _ref$highlightColor,
      _ref$margin = _ref.margin,
      margin = _ref$margin === void 0 ? '0' : _ref$margin,
      _ref$radius = _ref.radius,
      radius = _ref$radius === void 0 ? '4px' : _ref$radius,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? '100%' : _ref$width;
  return (
    /*#__PURE__*/
    React.createElement(StyledBone, {
      animate: animate,
      animDelay: animDelay,
      animSpeed: animSpeed,
      bgColor: bgColor,
      className: className,
      "data-testid": dataTestId,
      height: height,
      highlightColor: highlightColor,
      margin: margin,
      radius: radius,
      width: width
    })
  );
}

Bone.propTypes = {
  /** Whether or not the bone should be animated */
  animate: PropTypes.bool,

  /** The animation delay in seconds. Passed through to the CSS 'animation-delay' property */
  animDelay: PropTypes.number,

  /** The animation speed in seconds. Passed through to the CSS 'animation' property */
  animSpeed: PropTypes.number,

  /** The bone's background color.
   * Pass through to the CSS 'background-color' property
   * Default to grayLight.
   * */
  bgColor: PropTypes.string,

  /** Allows extending styles with styled(Bone) constructor. */
  className: PropTypes.string,

  /** Allows adding a data-testid to the parent div for testing purposes. */
  dataTestId: PropTypes.string,

  /** The bone height.
   * Passed though to the CSS 'height' property.
   * Because bones have no content we must generally specify a specific height in rems or pixels.
   */
  height: PropTypes.string,

  /** This is the primary color used in the animation.
   * **It must be a string in rgba format.**
   */
  highlightColor: PropTypes.string,

  /** Allows easy spacings between bones.
   * Passed through to the CSS 'margin' property.
   */
  margin: PropTypes.string,

  /** The corner radius to apply on the bone.
   * Passed through to the CSS 'border-radius' property.
   */
  radius: PropTypes.string,

  /** The bone width.
   * Passed though to the CSS 'width' property.
   */
  width: PropTypes.string
};
var flash = keyframes(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  to {\n    transform: translateX(100%);\n  }\n"])));
var StyledBone = styled.div(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  height: ", ";\n  width: ", ";\n  overflow: hidden;\n\n  background-color: ", ";\n  border-radius: ", ";\n  margin: ", ";\n  \n  ", "\n"])), function (p) {
  return p.height;
}, function (p) {
  return p.width;
}, function (p) {
  return p.bgColor;
}, function (p) {
  return p.radius;
}, function (p) {
  return p.margin;
}, function (p) {
  return p.animate && css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    position: relative;\n    &:after {\n      background-color: transparent;\n      position: absolute;\n      content: \"\";\n      width: 100%;\n      height: 100%;\n      transform: translateX(-100%);\n      background-position-x: right;\n      background: linear-gradient(\n        90deg,\n        ", ",\n        ", ",\n        ", "\n      );\n      animation: ", " ease-in-out ", "s infinite;\n      animation-delay: ", "s;\n    }\n  "])), changeOpacity(p.highlightColor, '0'), p.highlightColor, changeOpacity(p.highlightColor, '0'), flash, p.animSpeed, p.animDelay);
});
/**
 * Helper function that returns a new rgba color string
 * with an updated opacity.
 */

function changeOpacity(color, newOpacity) {
  var parts = color.replace(/[rgba() ]/g, '').split(',');
  parts[3] = newOpacity;
  return "rgba(".concat(parts.join(', '), ")");
}

export default Bone;